import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';

interface ClaimHeader {
  readonly claimId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef;
  readonly vendorId: string;
  readonly vendorCode: string | null;
  readonly vendorName: string | null;
  readonly status: string;
  /** None on a bill raised from Completed Jobs. */
  readonly billingPeriodFrom: string | null;
  readonly billingPeriodTo: string | null;
  // t150 stores these three only. Tiles for CGST/SGST/IGST/TDS/net payable
  // used to be declared here and could never render anything but a dash.
  readonly totalBasicAmount: number;
  readonly totalTaxAmount: number;
  readonly totalClaimAmount: number;
  /** GST on reverse-charge lines: inside the tax, outside the claim total. */
  readonly reverseChargeTaxAmount: number;
  readonly remarks: string;
  readonly createdAt: string;
  readonly submittedAt: string | null;
}

interface ClaimExpense {
  readonly expenseId: string;
  readonly expenseTypeName: string | null;
  readonly amount: number;
  readonly taxAmount: number;
  readonly isReverseCharge: boolean;
  readonly remarks: string | null;
}

interface LineItem {
  readonly lineId: string;
  readonly lineDescription: string | null;
  readonly workTypeCode: string | null;
  readonly customerName: string | null;
  readonly locationDesc: string | null;
  readonly serviceFrom: string | null;
  readonly serviceTo: string | null;
  readonly quantity: number | string | null;
  readonly areaSqft: number | string | null;
  readonly uomCode: string | null;
  readonly rateTypeCode: string | null;
  readonly ratePerUnit: number | string | null;
  readonly basicAmount: number | string | null;
  readonly taxAmount: number | string | null;
  readonly totalAmount: number | string | null;
  /** The line's tax is payable by the company, not the vendor. */
  readonly isReverseCharge: boolean;
}

const TILE_STYLE: React.CSSProperties = {
  padding: 16, borderRadius: 8,
  border: '1px solid var(--color-border-default, #d0d7de)',
  background: 'var(--color-bg-elevated, #fff)',
  textAlign: 'center', minWidth: 120,
};

const NUM_STYLE: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl, 28px)', fontWeight: 700,
  color: 'var(--color-brand-primary, #0969da)',
};

export function ClaimDetailPage(): ReactNode {
  const { day, dayRange } = useBusinessDay();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  const [claim, setClaim] = useState<ClaimHeader | null>(null);
  const [lines, setLines] = useState<LineItem[]>([]);
  const [expenses, setExpenses] = useState<ClaimExpense[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<ClaimHeader>(`/v1/billing/claims/${claimId}`, { token }),
      apiRequest<LineItem[]>(`/v1/billing/claims/${claimId}/lines`, { token }),
      apiRequest<ClaimExpense[]>(`/v1/billing/claims/${claimId}/expenses`, { token }),
    ])
      .then(([c, l, e]) => {
        if (!alive) return;
        setClaim(c); setLines(l); setExpenses(e);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, claimId]);

  if (error) return <main aria-label="claim-detail"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!claim) return <main aria-label="claim-detail"><p>Loading…</p></main>;

  const canWithdraw = claim.status === 'DRAFT' || claim.status === 'SUBMITTED';

  return (
    <main aria-label="claim-detail">
      <h1 style={{ marginBottom: 16 }}>{t('billing.claimDetail.title', { bill: billRefLabel(claim.bill, day) })}</h1>

      {/* ── Header info tiles ─────────────────────────── */}
      <section aria-label="claim-header" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        {([
          ['Status', claim.status],
          ['Vendor', claim.vendorName ?? claim.vendorCode ?? '—'],
          ['Vendor Code', claim.vendorCode ?? '—'],
          ['Period', dayRange(claim.billingPeriodFrom, claim.billingPeriodTo)],
        ] as const).map(([label, val]) => (
          <div key={label} style={TILE_STYLE}>
            <div style={{ ...NUM_STYLE, fontSize: 18 }}>{val}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>{label}</div>
          </div>
        ))}
        {([
          ['Basic', claim.totalBasicAmount],
          ['Tax', claim.totalTaxAmount],
          ['Total Claim', claim.totalClaimAmount],
        ] as const).map(([label, val]) => (
          <div key={label} style={TILE_STYLE}>
            <div style={NUM_STYLE}>₹{money(val)}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>{label}</div>
            {label === 'Tax' && claim.reverseChargeTaxAmount > 0 && (
              <div data-testid="claim-reverse-charge" style={{ fontSize: 12, marginTop: 4 }}>
                {t('billing.claimDetail.ofWhichRecipient', { amount: `₹${money(claim.reverseChargeTaxAmount)}` })}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* ── Actions ───────────────────────────────────── */}
      <section aria-label="claim-actions" style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {canWithdraw && (
          <Link to={`/billing/claims/${claimId}/withdraw`} data-testid="btn-withdraw-claim"
            style={{ padding: '8px 20px', borderRadius: 6, textDecoration: 'none', background: 'var(--color-danger)', color: 'var(--color-text-inverse)', fontWeight: 600 }}>
            Withdraw
          </Link>
        )}
        <Link to={`/billing/claims/${claimId}/bill`} data-testid="link-bill"
          style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid var(--color-border-default, #d0d7de)', textDecoration: 'none', fontWeight: 600 }}>
          View Bill
        </Link>
      </section>

      {/* ── Line items ────────────────────────────────── */}
      <section aria-label="claim-lines">
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Line Items</h2>
        {lines.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted, #656d76)' }}>No line items yet.</p>
        ) : (
          <table aria-label="claim-lines-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>#</th>
                <th style={{ padding: '8px 4px' }}>Description</th>
                <th style={{ padding: '8px 4px' }}>Dealer / Site</th>
                <th style={{ padding: '8px 4px' }}>Period</th>
                <th style={{ padding: '8px 4px' }}>Qty</th>
                <th style={{ padding: '8px 4px' }}>Rate</th>
                <th style={{ padding: '8px 4px' }}>Taxable (₹)</th>
                <th style={{ padding: '8px 4px' }}>Tax (₹)</th>
                <th style={{ padding: '8px 4px' }}>Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.lineId} aria-label={`line-${l.lineId}`}
                    style={{ borderBottom: '1px solid var(--color-border-default, #d0d7de)' }}>
                  <td style={{ padding: '8px 4px' }}>{i + 1}</td>
                  <td style={{ padding: '8px 4px' }}>{l.lineDescription ?? l.workTypeCode ?? '—'}</td>
                  <td style={{ padding: '8px 4px' }}>{[l.customerName, l.locationDesc].filter(Boolean).join(' — ') || '—'}</td>
                  <td style={{ padding: '8px 4px' }}>{dayRange(l.serviceFrom, l.serviceTo)}</td>
                  <td style={{ padding: '8px 4px' }}>{number(Number(l.areaSqft ?? l.quantity ?? 0))} {l.uomCode ?? l.rateTypeCode ?? ''}</td>
                  <td style={{ padding: '8px 4px' }}>₹{money(Number(l.ratePerUnit ?? 0))}</td>
                  <td style={{ padding: '8px 4px' }}>₹{money(Number(l.basicAmount ?? 0))}</td>
                  <td style={{ padding: '8px 4px' }}>
                    ₹{money(Number(l.taxAmount ?? 0))}
                    {l.isReverseCharge && (
                      <div data-testid={`line-reverse-charge-${l.lineId}`}
                        style={{ fontSize: 11, color: 'var(--color-text-muted, #656d76)' }}>
                        {t('billing.claimDetail.reverseCharge')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 4px' }}>₹{money(Number(l.totalAmount ?? 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Other expenses ────────────────────────────── */}
      <section aria-label="claim-expenses" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Other Expenses</h2>
        {expenses.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted, #656d76)' }}>
            None. A vendor enters these while making the bill, only when the work order allows them.
          </p>
        ) : (
          <table aria-label="claim-expenses-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>Type</th>
                <th style={{ padding: '8px 4px' }}>Remarks</th>
                <th style={{ padding: '8px 4px' }}>Amount (₹)</th>
                <th style={{ padding: '8px 4px' }}>GST (₹)</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.expenseId} aria-label={`expense-${e.expenseId}`}
                    style={{ borderBottom: '1px solid var(--color-border-default, #d0d7de)' }}>
                  <td style={{ padding: '8px 4px' }}>{e.expenseTypeName ?? '—'}</td>
                  <td style={{ padding: '8px 4px' }}>{e.remarks ?? '—'}</td>
                  <td style={{ padding: '8px 4px' }}>₹{money(e.amount)}</td>
                  <td style={{ padding: '8px 4px' }}>
                    ₹{money(e.taxAmount)}{e.isReverseCharge ? ' (reverse charge)' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {claim.remarks && (
        <section aria-label="claim-remarks" style={{ marginTop: 24, padding: 16, borderRadius: 8, border: '1px solid var(--color-border-default, #d0d7de)', background: 'var(--color-bg-elevated, #fff)' }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('billing.claimDetail.remarks')}</h2>
          <p>{claim.remarks}</p>
        </section>
      )}
    </main>
  );
}
